import { Form, Input } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { supplierApi } from '../../../../../apis'
import { ActionEnum, AlertMessageEnum } from '../../../../../common'
import {
  ManageModalComponent,
  ManageModalFooterComponent,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { SupplierEntity } from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadProfile,
  setAction,
  setActionProcessing,
  setAlert,
  setSelectedSupplier,
  setSuppliers,
} from '../../../../../stores/actions'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

const StyledLabel = styled.span`
  font-weight: bold;
`
type InputSupplier = {
  name: string
  address: string
  phone: string
}

export const ManageSupplierModalComponent: React.FC = () => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { selectedSupplier, suppliers } = useSelector(
    (state: RootState) => state.supplier
  )
  const { language } = useSelector((state: RootState) => state.language)
  const { management, regex } = useSelector((state: RootState) => state.common)
  const readOnlyMode = React.useMemo(
    () => [ActionEnum.VIEW, ActionEnum.DELETE].includes(management.action),
    [management]
  )

  const replaceSupplier = (supplier: SupplierEntity) => {
    const index = suppliers.map((item) => item.id).indexOf(supplier.id)
    if (index > -1) {
      dispatch(
        setSuppliers([
          ...suppliers.slice(0, index),
          supplier,
          ...suppliers.slice(index + 1),
        ])
      )
    }
  }

  const onFinish = async (value: InputSupplier) => {
    dispatch(setAlert())
    dispatch(setActionProcessing(true))
    switch (management.action) {
      case ActionEnum.VIEW:
        dispatch(setAction(ActionEnum.UPDATE))
        break
      case ActionEnum.UPDATE:
        if (selectedSupplier) {
          try {
            const respUpdate = await supplierApi.updateSupplier(
              selectedSupplier.id,
              value
            )
            replaceSupplier(respUpdate.data)
            dispatch(setSelectedSupplier())
            dispatch(
              setAlert({
                key: 'supplier.update.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e.response && e.response.status && e.response.status === 401) {
              dispatch(loadProfile())
              const respUpdate = await supplierApi.updateSupplier(
                selectedSupplier.id,
                value
              )
              replaceSupplier(respUpdate.data)
              dispatch(setSelectedSupplier())
              dispatch(
                setAlert({
                  key: 'supplier.update.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else if (
              e.response &&
              e.response.status &&
              e.response.status === 409
            ) {
              dispatch(
                setAlert({
                  key: `supplier.update.fail.duplicate.${e.response.data.error.field}`,
                  type: AlertMessageEnum.ERROR,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key: 'supplier.update.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      case ActionEnum.CREATE:
        try {
          const respCreate = await supplierApi.createSupplier(value)
          dispatch(setSuppliers([respCreate.data, ...suppliers]))
          dispatch(setSelectedSupplier())
          dispatch(
            setAlert({
              key: 'supplier.create.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        } catch (e) {
          if (e.response && e.response.status && e.response.status === 401) {
            dispatch(loadProfile())
            const respCreate = await supplierApi.createSupplier(value)
            dispatch(setSuppliers([respCreate.data, ...suppliers]))
            dispatch(setSelectedSupplier())
            dispatch(
              setAlert({
                key: 'supplier.create.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } else if (
            e.response &&
            e.response.status &&
            e.response.status === 409
          ) {
            dispatch(
              setAlert({
                key: `supplier.create.fail.duplicate.${e.response.data.error.field}`,
                type: AlertMessageEnum.ERROR,
              })
            )
          } else {
            dispatch(
              setAlert({
                key: 'supplier.create.fail.internal',
                type: AlertMessageEnum.ERROR,
              })
            )
          }
        }
        break
      case ActionEnum.DELETE:
        if (selectedSupplier) {
          try {
            await supplierApi.deleteSupplier(selectedSupplier.id)
            dispatch(
              setSuppliers(
                suppliers.filter((item) => item.id !== selectedSupplier.id)
              )
            )
            dispatch(setSelectedSupplier())
            dispatch(
              setAlert({
                key: 'supplier.delete.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e.response && e.response.status && e.response.status === 401) {
              dispatch(loadProfile())
              await supplierApi.deleteSupplier(selectedSupplier.id)
              dispatch(
                setSuppliers(
                  suppliers.filter((item) => item.id !== selectedSupplier.id)
                )
              )
              dispatch(setSelectedSupplier())
              dispatch(
                setAlert({
                  key: 'supplier.delete.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key:
                    e.response && e.response.status && e.response.status === 400
                      ? 'common.delete.fail.fkConstraint'
                      : 'supplier.delete.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      default:
        break
    }
    dispatch(setActionProcessing(false))
  }

  const body = (
    <Form onFinish={onFinish} form={form}>
      {(!management.alertMessage ||
        management.alertMessage.type !== AlertMessageEnum.SUCCESS) && (
        <>
          {(readOnlyMode || management.action === ActionEnum.UPDATE) && (
            <StyledFormItem
              label={
                <StyledLabel>{translate('supplier.id', language)}</StyledLabel>
              }
            >
              {selectedSupplier?.id}
            </StyledFormItem>
          )}
          {readOnlyMode ? (
            <>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('supplier.name', language)}
                  </StyledLabel>
                }
              >
                {selectedSupplier?.name}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('supplier.address', language)}
                  </StyledLabel>
                }
              >
                {selectedSupplier?.address}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('supplier.phone', language)}
                  </StyledLabel>
                }
              >
                {selectedSupplier?.phone}
              </StyledFormItem>
            </>
          ) : (
            <>
              <StyledFormItem
                name="name"
                label={
                  <StyledLabel>
                    {translate('supplier.name', language)}
                  </StyledLabel>
                }
                initialValue={selectedSupplier?.name}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value || !value.trim().length) {
                        return Promise.reject(
                          new Error(
                            translate('supplier.invalid.name', language)
                          )
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  placeholder={translate('supplier.name', language)}
                  allowClear
                />
              </StyledFormItem>
              <StyledFormItem
                name="address"
                label={
                  <StyledLabel>
                    {translate('supplier.address', language)}
                  </StyledLabel>
                }
                initialValue={selectedSupplier?.address}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value || !value.trim().length) {
                        return Promise.reject(
                          new Error(
                            translate('supplier.invalid.address', language)
                          )
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  placeholder={translate('supplier.address', language)}
                  allowClear
                />
              </StyledFormItem>
              <StyledFormItem
                name="phone"
                label={
                  <StyledLabel>
                    {translate('supplier.phone', language)}
                  </StyledLabel>
                }
                initialValue={selectedSupplier?.phone}
                rules={[
                  {
                    required: true,
                    message: translate('supplier.invalid.phone', language),
                    pattern: regex.phone,
                  },
                ]}
              >
                <Input
                  placeholder={translate('supplier.phone', language)}
                  allowClear
                />
              </StyledFormItem>
            </>
          )}
        </>
      )}
      <ManageModalFooterComponent
        hasDefaultButtons
        onDeleteConfirm={() => form.submit()}
      />
    </Form>
  )

  return <ManageModalComponent body={body} />
}
